import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { parseWarehouseSync, warehouseSyncKeys, type WarehouseSync } from './entity';

const STALE_TIME = 60_000;

/**
 * @public The workspace's warehouse-sync config, or `null` when none is
 * configured. The not-configured error is swallowed and treated as "no sync"
 * rather than surfaced as a failure.
 */
export async function getWarehouseSync(workspaceId: number, qc: QueryClient): Promise<WarehouseSync | null> {
  try {
    const data = await hydraUiApi.warehouse.getWarehouseSync({
      params: { path: { environment_id: String(workspaceId) } },
    });
    if (data.warehouse_sync == null) {
      return null;
    }
    const parsed = parseWarehouseSync(data.warehouse_sync);
    qc.setQueryData(warehouseSyncKeys.detail(workspaceId, 'current'), parsed);
    return parsed;
  } catch {
    return null;
  }
}

/** @public */
export const getWarehouseSyncQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: warehouseSyncKeys.detail(workspaceId, 'current'),
    queryFn: () => getWarehouseSync(workspaceId, qc),
    staleTime: STALE_TIME,
  });

/** @public */
export const useWarehouseSync = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getWarehouseSyncQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({ ...getWarehouseSyncQueryOptions(workspaceId, qc), ...queryConfig });
};
